// Client-side cryptography for the upload SPA.
//
// Three operations:
//   1. verifyPrekey({kem_pk, signature, id_pk})
//        ML-DSA-65 verify over the bare 1184-byte kem_pk. Throws on bad sig.
//   2. encapsulate(kem_pk) -> {ciphertext, sharedSecret}
//        ML-KEM-768 encapsulation. ciphertext goes to the server; sharedSecret
//        becomes the AES key.
//   3. encryptAesGcm(sharedSecret, plaintext) -> {iv, encrypted}
//        AES-256-GCM with a fresh 12-byte random IV, no AAD. WebCrypto's
//        output already concatenates the 16-byte GCM tag at the end, which
//        is exactly the wire format the .NET AesGcmPayload.Decrypt expects.

import { ml_dsa65 } from '@noble/post-quantum/ml-dsa.js';
import { ml_kem768 } from '@noble/post-quantum/ml-kem.js';

export class IntegrityError extends Error {
  constructor(message = 'pre-key signature failed verification') {
    super(message);
    this.name = 'IntegrityError';
  }
}

/**
 * Verify the ML-DSA-65 signature carried with the pre-key.
 * Each input is a Uint8Array (decode from base64 BEFORE calling this).
 * Throws IntegrityError on failure.
 *
 * @noble's API is verify(publicKey, message, signature) -- DO NOT swap.
 * The .NET signer (TestAccount.cs / PreKeyService) signs the bare 1184-byte
 * kem_pk with no AAD, matching @noble's default empty ctx.
 */
export function verifyPrekey({ kem_pk, signature, id_pk }) {
  const ok = ml_dsa65.verify(id_pk, kem_pk, signature);
  if (!ok) {
    throw new IntegrityError();
  }
}

/**
 * ML-KEM-768 encapsulation against the recipient's pre-key.
 * Returns the ciphertext to ship to the server and the 32-byte shared
 * secret to use as the AES-256 key.
 */
export function encapsulate(kem_pk) {
  const { cipherText, sharedSecret } = ml_kem768.encapsulate(kem_pk);
  return { ciphertext: cipherText, sharedSecret };
}

/**
 * AES-256-GCM encrypt the given plaintext with the shared secret.
 * Generates a fresh 12-byte IV. No AAD.
 *
 * Returns { iv, encrypted } where:
 *   - iv is a 12-byte Uint8Array (the nonce)
 *   - encrypted is a Uint8Array of [ciphertext || 16-byte tag]
 *     (WebCrypto native layout — same as .NET AesGcmPayload writes)
 */
export async function encryptAesGcm(sharedSecret, plaintext) {
  const key = await crypto.subtle.importKey(
    'raw',
    sharedSecret,
    { name: 'AES-GCM' },
    false,
    ['encrypt'],
  );
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cipherBuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    key,
    plaintext,
  );
  return { iv, encrypted: new Uint8Array(cipherBuf) };
}

/** Encode a Uint8Array to base64 (browser-safe, no Node Buffer). */
export function toBase64(bytes) {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) {
    bin += String.fromCharCode(bytes[i]);
  }
  return btoa(bin);
}

/** Decode a base64 string to Uint8Array. */
export function fromBase64(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
}
